<template>
  <v-dialog v-model="dialog" persistent max-width="420px">
    <v-card grid-list-md>
      <ModelTitle title="New Term" @close="close()" />

      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="Term.name"
              outlined
              autofocus
              dense
              label="Name"
              :hide-details="nameErrors.length === 0"
              :error-messages="nameErrors"
            />
          </v-col>

          <v-col cols="12">
            <v-autocomplete
              v-model="Term.YearId"
              dense
              outlined
              clearable
              item-text="name"
              item-value="id"
              :items="years"
              :loading="loading"
              :disabled="isYearLocked"
              :hide-details="yearErrors.length === 0"
              :error-messages="yearErrors"
              label="Select Year"
            >
              <template slot="item" slot-scope="{ item }">
                <v-list-item-content>
                  <v-list-item-title class="text--primary text-capitalize">
                    {{ item.name }}
                  </v-list-item-title>

                  <v-list-item-subtitle class="caption" v-if="item.Class">
                    {{ item.Class.name }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>

              <template slot="selection" slot-scope="{ item }">
                <span>
                  {{ item.name }}
                  <span v-if="item.Class">, {{ item.Class.name }}</span>
                </span>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Term.startDate"
              type="date"
              outlined
              dense
              label="Start Date *"
              :hide-details="startDateErrors.length === 0"
              :error-messages="startDateErrors"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="Term.endDate"
              type="date"
              outlined
              dense
              label="End Date *"
              :hide-details="endDateErrors.length === 0"
              :error-messages="endDateErrors"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="secondary" text @click="close()">Close</v-btn>
        <v-btn color="primary" :loading="loading" dark @click="submit()">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";
import termService from "../service.js";
import yearService from "@/modules/ReportCard/Year/service.js";

const { validationMixin } = require("vuelidate");
const { required } = require("vuelidate/lib/validators");

export default {
  name: "create-payee",
  props: {
    createDialog: {
      default: false,
    },
    createFromStore: {
      type: Boolean,
    },
    selectedYear: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      years: [],
      loading: false,
      dialog: false,
      Term: {
        name: null,
        YearId: null,
        startDate: null,
        endDate: null,
      },
    };
  },
  mixins: [validationMixin],
  validations: {
    Term: {
      name: {
        required,
      },
      YearId: {
        required,
      },
      startDate: {
        required,
      },
      endDate: {
        required,
      },
    },
  },
  computed: {
    ...mapGetters("global", ["currentUser"]),

    isYearLocked() {
      return !!(this.selectedYear && this.selectedYear.id);
    },

    nameErrors() {
      const errors = [];
      if (!this.$v.Term.name.$dirty) return errors;
      if (!this.$v.Term.name.required) {
        errors.push(this.$t("validations.fieldIsRequired"));
      }
      return errors;
    },

    yearErrors() {
      const errors = [];
      if (!this.$v.Term.YearId.$dirty) return errors;
      if (!this.$v.Term.YearId.required) {
        errors.push(this.$t("validations.fieldIsRequired"));
      }
      return errors;
    },

    startDateErrors() {
      const errors = [];
      if (!this.$v.Term.startDate.$dirty) return errors;
      if (!this.$v.Term.startDate.required) {
        errors.push("Start date is required");
        return errors;
      }
      if (this.Term.startDate && this.Term.endDate && this.Term.startDate > this.Term.endDate) {
        errors.push("Start date cannot be after end date");
      }
      return errors;
    },

    endDateErrors() {
      const errors = [];
      if (!this.$v.Term.endDate.$dirty) return errors;
      if (!this.$v.Term.endDate.required) {
        errors.push("End date is required");
        return errors;
      }
      if (this.Term.startDate && this.Term.endDate && this.Term.startDate > this.Term.endDate) {
        errors.push("End date cannot be before start date");
      }
      return errors;
    },
  },
  watch: {
    createDialog(val) {
      this.dialog = val;

      if (val) {
        this.getYear().then(() => {
          this.applySelectedYear();
        });
      }
    },

    selectedYear: {
      deep: true,
      handler() {
        if (this.dialog) {
          this.applySelectedYear();
        }
      },
    },
  },
  methods: {
    async getYear() {
      this.loading = true;
      try {
        const response = await yearService.getAll();
        this.years = response.data || [];
        return response;
      } catch (error) {
        console.log("error getting year", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    applySelectedYear() {
      if (this.selectedYear && this.selectedYear.id) {
        this.Term.YearId = this.selectedYear.id;
      }
    },

    close() {
      this.empty();
      this.$emit("close");
    },

    empty() {
      this.Term = {
        name: null,
        YearId: null,
        startDate: null,
        endDate: null,
      };
      this.$v.$reset();
    },

    submit() {
      this.$v.$touch();

      if (this.$v.$invalid || (this.Term.startDate && this.Term.endDate && this.Term.startDate > this.Term.endDate)) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });
        return;
      }

      this.loading = true;

      return termService
        .create(this.Term)
        .then((result) => {
          if (result.status) {
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "success",
                title: "Term is created",
              });

            this.loading = false;
            this.$emit("submit");
            this.close();
          }
        })
        .catch((err) => {
          this.loading = false;

          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: err.data.message,
            });
        });
    },
  },
};
</script>

<style scoped></style>
